import { useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSession } from '../auth/session'
import { currentItem, visibleNavigation } from './navigation'
import { isCurrentPage, sectionsByModule } from './sections'
import { Logo } from '../components/Logo'
import { NavIcon } from './NavIcon'

/**
 * The menu (D-115): navy, DP Light's logo at the head, each item with its icon, the
 * current one a blue pill with its module's pages beneath it. Who is signed in, their
 * PIN and signing out are in the header's account menu.
 *
 * From lg up it is always there. Below that it is a drawer over the page: closed it
 * is invisible as well as off-screen, so a keyboard or a screen reader cannot wander
 * into links nobody can see.
 */
export function Sidebar({
  open = false,
  onNavigate,
  onClose,
}: {
  open?: boolean
  onNavigate?: () => void
  onClose?: () => void
}) {
  const can = useSession((s) => s.can)
  const closeButton = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    closeButton.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose?.()
    }
    // The page behind the open drawer stays where it was.
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [open, onClose])

  const { pathname } = useLocation()
  const visible = visibleNavigation(can)
  const here = currentItem(pathname)

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" aria-hidden="true" onClick={onClose} />}

      <aside
        id="app-sidebar"
        className={[
          'fixed inset-y-0 left-0 z-40 flex w-sidebar shrink-0 flex-col bg-dpl-nav text-white transition-[transform,visibility] duration-200',
          'lg:static lg:visible lg:z-auto lg:translate-x-0 lg:shadow-none lg:transition-none',
          open ? 'visible translate-x-0 shadow-xl' : 'invisible -translate-x-full',
        ].join(' ')}
      >
        <div className="flex items-center gap-2 px-4 py-5">
          <div className="min-w-0 flex-1">
            <Logo size={48} />
            <span className="sr-only">DP Light Business Platform</span>
          </div>
          <button
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="-mr-2 flex h-9 w-9 items-center justify-center rounded-md text-xl text-white/75 hover:bg-white/10 hover:text-white lg:hidden"
          >
            ×
          </button>
        </div>

        <nav aria-label="Main" className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {visible.map((item) => {
            const current = here?.icon === item.icon
            const pages = current ? (sectionsByModule[item.icon]?.(can) ?? []) : []
            return (
              <div key={item.icon}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={current ? 'page' : undefined}
                  className={[
                    'flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[15px] transition',
                    current
                      ? 'bg-dpl-primary font-medium text-white shadow-sm'
                      : 'text-white/80 hover:bg-dpl-nav-hover hover:text-white',
                  ].join(' ')}
                >
                  <NavIcon name={item.icon} />
                  {item.label}
                </Link>
                {pages.length > 1 && (
                  <ul aria-label={`${item.label} pages`} className="mb-2 ml-6 mt-1.5 space-y-0.5 border-l border-white/10 pl-3">
                    {pages.map((page) => {
                      const on = isCurrentPage(page, pathname)
                      return (
                        <li key={page.to}>
                          <Link
                            to={page.to}
                            onClick={onNavigate}
                            aria-current={on ? 'page' : undefined}
                            className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition ${on ? 'bg-white/10 font-medium text-white' : 'text-white/65 hover:text-white'}`}
                          >
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${on ? 'bg-dpl-accent' : 'bg-white/40'}`} aria-hidden="true" />
                            {page.label}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )
          })}
        </nav>
      </aside>
    </>
  )
}
